import type { ActivityLogItem } from '@ayub/shared'
import { BLOGGER_STAGE_LABELS, formatNumber, formatSom, REJECT_REASON_LABELS } from '@ayub/shared'
import { PAYOUT_TYPE_LABELS } from '@/components/payouts/statusMeta'

/**
 * `ActivityLogItem.action` + `oldValue`/`newValue`ni odam o'qiydigan o'zbekcha matnga
 * o'giradi — SOF funksiyalar (hech qanday tarmoq/DOM chaqiruvi yo'q), shuning uchun
 * `HistorySection.tsx`dan mustaqil tekshirish/rivojlantirish mumkin.
 *
 * `oldValue`/`newValue` server tomonida ATAYLAB `unknown` (`packages/shared/src/api.ts`
 * izohiga qarang: "har amal o'z shaklini yozadi"). Bu yerda HAR BIR `action` uchun ANIQ
 * shaklni bilamiz (server kodidan — `apps/server/src/services/*.ts`,
 * `apps/server/src/jobs/*.ts`, `apps/server/src/bot/handlers/*.ts` dagi barcha
 * `activityLog.create()` chaqiruvlari qo'lda o'qib chiqilgan), lekin baribir HAR BIR
 * maydonni xavfsiz (`typeof` tekshiruvi bilan) o'qiymiz — kelajakda kimdir shaklni
 * o'zgartirsa, UI yiqilib tushmasin, faqat matn kamroq aniq bo'lib qoladi.
 *
 * Diqqat: bu fayl JSX EMAS, oddiy TypeScript — shuning uchun tutuq belgisi (') to'g'ridan-
 * to'g'ri ishlatiladi (`&apos;` FAQAT JSX matnida dekodlanadi, oddiy stringda esa XOM matn
 * bo'lib chiqadi), qolgan loyiha bo'ylab (masalan `services/videos.ts`dagi xato xabarlari)
 * ishlatilgan naqsh: apostrof bo'lgan matn qo'sh tirnoq ichida yoziladi.
 */

// ─────────────────────────────── Xavfsiz o'qish yordamchilari ───────────────────────────────

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null) return null
  return value as Record<string, unknown>
}

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/** `REJECT_REASON_LABELS`da yo'q (masalan kelajakda qo'shilgan) sabab uchun xom qiymat qaytadi. */
function rejectReasonLabel(reason: string): string {
  return (REJECT_REASON_LABELS as Record<string, string>)[reason] ?? reason
}

function bloggerStageLabel(stage: string): string {
  return (BLOGGER_STAGE_LABELS as Record<string, string>)[stage] ?? stage
}

function payoutTypeLabel(type: string): string {
  return (PAYOUT_TYPE_LABELS as Record<string, string>)[type] ?? type
}

/** `note` maydoni bo'lsa " — <note>" qo'shib qaytaradi, aks holda matnni o'zgartirmaydi. */
function withNote(text: string, note: string | null): string {
  return note && note.trim().length > 0 ? `${text} — ${note.trim()}` : text
}

// ─────────────────────────────── Aktyor ───────────────────────────────

/** Qator ustidagi "kim qildi" — admin ismi, "Tizim" (cron) yoki "Bloger". */
export function describeActor(item: ActivityLogItem): string {
  if (item.actorType === 'system') return 'Tizim'
  if (item.actorType === 'blogger') return 'Bloger'
  return item.actorName ?? 'Admin'
}

// ─────────────────────────────── Noma'lum action uchun zaxira ───────────────────────────────

/** JSON'ni qisqartiradi — uzun `oldValue`/`newValue` qatorni buzmasin. */
function truncateJson(value: unknown, maxLength = 80): string {
  if (value === undefined || value === null) return ''
  let json: string
  try {
    json = JSON.stringify(value)
  } catch {
    return ''
  }
  if (json.length <= maxLength) return json
  return `${json.slice(0, maxLength)}…`
}

/**
 * Yuqoridagi `describeActivity()` bilmaydigan (kelajakda qo'shilgan) `action` uchun —
 * UI hech qachon "bo'sh qator" ko'rsatmasin, kamida action nomi + qiymatlar ko'rinadi.
 */
function describeUnknown(item: ActivityLogItem): string {
  const parts = [item.action]
  const old = truncateJson(item.oldValue)
  const next = truncateJson(item.newValue)
  if (old || next) {
    parts.push(`(${old || '—'} → ${next || '—'})`)
  }
  return parts.join(' ')
}

// ─────────────────────────────── Bloger amallari ───────────────────────────────

function describeBloggerActivity(item: ActivityLogItem): string | null {
  const oldRec = asRecord(item.oldValue)
  const newRec = asRecord(item.newValue)

  switch (item.action) {
    case 'application_submitted': {
      const fullName = newRec ? str(newRec.fullName) : null
      const instagram = newRec ? str(newRec.instagramUsername) : null
      const followers = newRec ? num(newRec.followers) : null
      const details = [
        fullName,
        instagram ? `@${instagram}` : null,
        followers !== null ? `${formatNumber(followers)} obunachi` : null,
      ].filter((part): part is string => part !== null)
      return details.length > 0
        ? `Yangi ariza yuborildi: ${details.join(', ')}`
        : 'Yangi ariza yuborildi'
    }
    case 'stage_changed': {
      const trigger = newRec ? str(newRec.trigger) : null
      if (trigger === 'reapply') return 'Ariza qayta yuborildi (avval rad etilgan edi)'
      const oldStage = oldRec ? str(oldRec.stage) : null
      const newStage = newRec ? str(newRec.stage) : null
      if (oldStage && newStage) {
        return `Bosqich o'zgardi: ${bloggerStageLabel(oldStage)} → ${bloggerStageLabel(newStage)}`
      }
      return "Bosqich o'zgardi"
    }
    case 'approved':
      return 'Ariza tasdiqlandi'
    case 'rejected': {
      const reason = newRec ? str(newRec.reason) : null
      const note = newRec ? str(newRec.note) : null
      const text = reason ? `Ariza rad etildi: ${rejectReasonLabel(reason)}` : 'Ariza rad etildi'
      return withNote(text, note)
    }
    case 'blocked': {
      const note = newRec ? str(newRec.note) : null
      return withNote('Bloger bloklandi', note)
    }
    case 'unblocked':
      return 'Bloger blokdan chiqarildi'
    case 'card_viewed':
      return "Karta raqami ko'rildi"
    case 'card_saved':
      return oldRec ? "Karta ma'lumoti yangilandi" : "Karta ma'lumoti kiritildi"
    default:
      return null
  }
}

// ─────────────────────────────── Video amallari ───────────────────────────────

function describeVideoActivity(item: ActivityLogItem): string | null {
  const oldRec = asRecord(item.oldValue)
  const newRec = asRecord(item.newValue)

  switch (item.action) {
    case 'submitted':
      return 'Video yuborildi'
    case 'link_submitted':
      return 'Instagram havolasi yuborildi'
    case 'link_verified':
      return "Havola tekshirildi — to'g'ri"
    case 'link_rejected': {
      const reason = newRec ? str(newRec.reason) : null
      const note = newRec ? str(newRec.note) : null
      const text = reason ? `Havola rad etildi: ${rejectReasonLabel(reason)}` : 'Havola rad etildi'
      return withNote(text, note)
    }
    case 'approved':
      return 'Video tasdiqlandi'
    case 'rejected': {
      const reason = newRec ? str(newRec.reason) : null
      const note = newRec ? str(newRec.note) : null
      const text = reason ? `Video rad etildi: ${rejectReasonLabel(reason)}` : 'Video rad etildi'
      return withNote(text, note)
    }
    case 'checking_started':
      return 'Tekshiruv boshlandi (tekshiruv kutilmoqda)'
    case 'check_recorded':
    case 'check_finalized': {
      const views = newRec ? num(newRec.views) : null
      const amount = newRec ? num(newRec.amount) : null
      if (views !== null && amount !== null) {
        return `Tekshiruv (yakuniy) kiritildi: ${formatNumber(views)} · ${formatSom(amount)}`
      }
      return 'Tekshiruv (yakuniy) kiritildi'
    }
    case 'check_corrected': {
      const oldViews = oldRec ? num(oldRec.views) : null
      const newViews = newRec ? num(newRec.views) : null
      const note = newRec ? str(newRec.note) : null
      const text =
        oldViews !== null && newViews !== null
          ? `Tekshiruv tuzatildi: ${formatNumber(oldViews)} → ${formatNumber(newViews)}`
          : 'Tekshiruv tuzatildi'
      return withNote(text, note)
    }
    case 'paid':
      return "To'lovga bog'landi — to'landi"
    case 'link_reminder_24h':
      return 'Havola haqida eslatma yuborildi (24 soat)'
    case 'link_reminder_final':
      return 'Havola haqida oxirgi ogohlantirish yuborildi (3 kun qoldi)'
    case 'link_auto_rejected':
      return 'Havola muddatida yuborilmadi — avtomatik rad etildi'
    default:
      return null
  }
}

// ─────────────────────────────── To'lov amallari ───────────────────────────────

function describePayoutActivity(item: ActivityLogItem): string | null {
  const newRec = asRecord(item.newValue)

  switch (item.action) {
    case 'card_viewed':
      return "Karta raqami ko'rildi"
    case 'requested': {
      const type = newRec ? str(newRec.type) : null
      const amount = newRec ? num(newRec.amount) : null
      const videoCount = newRec ? num(newRec.videoCount) : null
      const parts = [
        amount !== null ? formatSom(amount) : null,
        videoCount !== null ? `${videoCount} ta video` : null,
        type ? payoutTypeLabel(type) : null,
      ].filter((part): part is string => part !== null)
      return parts.length > 0
        ? `To'lov so'rovi yaratildi: ${parts.join(', ')}`
        : "To'lov so'rovi yaratildi"
    }
    case 'paid': {
      const amount = newRec ? num(newRec.amount) : null
      return amount !== null
        ? `To'lov amalga oshirildi: ${formatSom(amount)}`
        : "To'lov amalga oshirildi"
    }
    case 'cancelled': {
      const note = newRec ? str(newRec.note) : null
      return withNote("To'lov bekor qilindi", note)
    }
    default:
      return null
  }
}

// ─────────────────────────────── Admin/sozlama/xabarnoma amallari ───────────────────────────────

function describeAdminActivity(item: ActivityLogItem): string | null {
  const oldRec = asRecord(item.oldValue)
  const newRec = asRecord(item.newValue)

  switch (item.action) {
    case 'created': {
      const name = newRec ? str(newRec.name) : null
      return name ? `Admin qo'shildi: ${name}` : "Admin qo'shildi"
    }
    case 'deleted': {
      const name = oldRec ? str(oldRec.name) : null
      return name ? `Admin o'chirildi: ${name}` : "Admin o'chirildi"
    }
    default:
      return null
  }
}

function describeSettingActivity(item: ActivityLogItem): string | null {
  const oldRec = asRecord(item.oldValue)
  const newRec = asRecord(item.newValue)

  if (item.action !== 'updated') return null
  const key = newRec ? str(newRec.key) : null
  const oldVal = oldRec ? oldRec.value : undefined
  const newVal = newRec ? newRec.value : undefined
  if (key) {
    return `Sozlama o'zgardi (${key}): ${truncateJson(oldVal, 40)} → ${truncateJson(newVal, 40)}`
  }
  return "Sozlama o'zgartirildi"
}

function describeBroadcastActivity(item: ActivityLogItem): string | null {
  const newRec = asRecord(item.newValue)

  switch (item.action) {
    case 'created': {
      const totalCount = newRec ? num(newRec.totalCount) : null
      return totalCount !== null
        ? `Xabar yuborish boshlandi: ${formatNumber(totalCount)} ta qabul qiluvchi`
        : 'Xabar yuborish boshlandi'
    }
    case 'sent': {
      const sentCount = newRec ? num(newRec.sentCount) : null
      const failedCount = newRec ? num(newRec.failedCount) : null
      if (sentCount !== null && failedCount !== null) {
        return `Xabar yuborish yakunlandi: ${formatNumber(sentCount)} yetkazildi, ${formatNumber(failedCount)} muvaffaqiyatsiz`
      }
      return 'Xabar yuborish yakunlandi'
    }
    default:
      return null
  }
}

// ─────────────────────────────── Umumiy kirish nuqtasi ───────────────────────────────

/**
 * Amal tavsifini `entityType`ga qarab tegishli bo'limga yo'naltiradi. `action` nomlari
 * turkumlar orasida TAKRORLANADI (masalan `approved`/`rejected`/`paid`/`card_viewed`
 * bloger/video/to'lovda ham bor, lekin ma'nosi boshqa) — shuning uchun avval `entityType`
 * bo'yicha tarmoqlanadi, ANIQ `action` string bo'yicha emas.
 */
export function describeActivity(item: ActivityLogItem): string {
  let text: string | null = null
  switch (item.entityType) {
    case 'blogger':
      text = describeBloggerActivity(item)
      break
    case 'video':
      text = describeVideoActivity(item)
      break
    case 'payout':
      text = describePayoutActivity(item)
      break
    case 'admin':
      text = describeAdminActivity(item)
      break
    case 'setting':
      text = describeSettingActivity(item)
      break
    case 'broadcast':
      text = describeBroadcastActivity(item)
      break
    default:
      text = null
  }
  return text ?? describeUnknown(item)
}
